import { Title } from "lola-framework-ui-test";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { MailStrokeIcon, WhatsAppStrokeIcon } from "../assets/icons";
import { CloseLight } from "../assets/navIcons";
import { TITLE_COLOR } from "../constants";
import { useStore } from "../store/globalState";
import { BodyCopy } from "./BodyCopy";
import { Popup } from "./Popup";
import { Toast } from "./Toast";

export interface MaintenanceSupportChannels {
  email?: string;
  whatsapp?: string;
}

/**
 * Same rows as SupportSheet. Channels come from the maintenance payload,
 * not from the session.
 */
export function MaintenanceSupportSheet({
  visible,
  onClose,
  channels,
}: {
  visible: boolean;
  onClose: () => void;
  channels: MaintenanceSupportChannels;
}) {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const [showToast, setShowToast] = useState(false);
  const email = channels.email;
  const whatsapp = channels.whatsapp;

  const goToWhatsApp = useCallback(() => {
    if (!whatsapp) return;
    window.open(
      `https://wa.me/${whatsapp.replace(/\D/g, "")}`,
      "_blank",
      "noopener,noreferrer",
    );
  }, [whatsapp]);

  const sendEmail = useCallback(async () => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setShowToast(true);
    } catch {
      // Clipboard may be unavailable; still open the mail client.
    }
    window.open(`mailto:${email}`, "_blank", "noopener,noreferrer");
  }, [email]);

  return (
    <>
      <Popup visible={visible} onClose={onClose}>
        <section className="support-sheet">
          <header className="payout-details-header">
            <span className="payout-header-action" aria-hidden="true" />
            <Title title={t("contact.chooseOption")} align="center" color={bodyCopy} />
            <button
              type="button"
              className="payout-header-action payout-close-btn"
              aria-label={t("common.cancel")}
              onClick={onClose}
            >
              <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
            </button>
          </header>
          {email && (
            <button
              type="button"
              className="support-sheet-option"
              onClick={() => void sendEmail()}
            >
              <span
                className="support-sheet-option-icon"
                style={{ background: backgroundBtn }}
              >
                <MailStrokeIcon size={22} colors={[textColorBtn, textColorBtn]} />
              </span>
              <span style={{ color: TITLE_COLOR }}>{t("contact.email")}</span>
            </button>
          )}
          {whatsapp && (
            <button type="button" className="support-sheet-option" onClick={goToWhatsApp}>
              <span
                className="support-sheet-option-icon"
                style={{ background: backgroundBtn }}
              >
                <WhatsAppStrokeIcon size={22} colors={[textColorBtn, textColorBtn]} />
              </span>
              <span style={{ color: TITLE_COLOR }}>{t("contact.whatsapp")}</span>
            </button>
          )}
          {!email && !whatsapp && (
            <BodyCopy className="footer-text muted-foreground">
              {t("contact.empty")}
            </BodyCopy>
          )}
        </section>
      </Popup>
      <section className="toast-container">
        {showToast && (
          <Toast
            message={t("contact.emailCopied")}
            duration={2000}
            type="success"
            onClose={() => setShowToast(false)}
          />
        )}
      </section>
    </>
  );
}
